import { useRef, useState, useEffect } from "react";
import { format } from "date-fns";
import { uniqueId } from "lodash";
import { IconButton, MenuItem, Select, SelectChangeEvent, Stack, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { ArrowDropDownRounded, ContentCopyRounded } from "@mui/icons-material";
import momentData from "moment-timezone/data/meta/latest.json";
import TextLink from "../../../../components/TextLink";
import Search from "../../Search";
import { fonts } from "../../../../theme";
import { dataGridStyles } from "../../styles";
import { firebaseClient } from "../../../../helpers/apolloClients";
import { useGetAdminArtistsQuery } from "../../../../generated/firebase";
import { useDebounce } from "react-use";

const Artists = () => {
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [searchValue, setSearchValue] = useState<string>("");
  const [searchValueNormalized, setSearchValueNormalized] = useState<string>("");
  const gridRef = useRef<HTMLDivElement>(null);
  const sortedCountries = Object.values(momentData.countries).sort((a, b) => a.name.localeCompare(b.name));
  const pageSize = 24;

  const { data, loading, fetchMore } = useGetAdminArtistsQuery({
    client: firebaseClient,
    variables: {
      limit: pageSize,
      country: selectedCountry,
      query: searchValueNormalized,
    },
    notifyOnNetworkStatusChange: true,
  });
  const artists = data?.artists?.nodes || [];
  const hasMoreArtists = data?.artists?.pageInfo.hasNextPage || false;

  const onCountryChange = (event: SelectChangeEvent<string>) => {
    setSelectedCountry(event.target.value);
  };

  useDebounce(() => setSearchValueNormalized(searchValue.toLowerCase()), 1000, [searchValue]);

  const loadMore = async () => {
    await fetchMore({
      variables: {
        afterCursor: artists[artists.length - 1].id,
      },
    });
  };

  useEffect(() => {
    const pages = Math.ceil(artists.length / pageSize);
    if (page > 0 && page > pages - 1) {
      loadMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      renderCell: ({ row }) => (
        <Stack
          direction="row"
          alignItems="center"
          onClick={() => navigator.clipboard.writeText(row.email)}
          sx={{ cursor: "pointer" }}
        >
          <Typography fontSize={14} fontFamily={fonts.body}>
            {row.email}
          </Typography>
          <IconButton>
            <ContentCopyRounded color="primary" sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      ),
    },
    {
      field: "paypalEmail",
      headerName: "Paypal Email",
      flex: 1,
      renderCell: ({ row }) => (
        <Stack
          direction="row"
          alignItems="center"
          onClick={() => navigator.clipboard.writeText(row.paypalEmail)}
          sx={{ cursor: "pointer" }}
        >
          <Typography fontSize={14} fontFamily={fonts.body}>
            {row.paypalEmail}
          </Typography>
          <IconButton>
            <ContentCopyRounded color="primary" sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      ),
    },
    {
      field: "city",
      headerName: "City",
    },
    {
      field: "country",
      headerName: "Country",
    },
    {
      field: "vatNumber",
      headerName: "VAT/GST Number",
    },
    {
      field: "lastUploaded",
      headerName: "Last Uploaded",
      renderCell: (params) => (
        <Typography fontSize={14} fontFamily={fonts.body}>
          {params.row.lastUploaded ? format(params.row.lastUploaded, "do MMM yyyy") : ""}
        </Typography>
      ),
    },
    {
      field: "artistPage",
      headerName: "Profile Url",
      renderCell: (params) => (
        <TextLink openInNewTab href={`${process.env.REACT_APP_SITE_URL}/artists/${params.row.permalink}`}>
          <Typography fontSize={14} fontFamily={fonts.body}>
            View
          </Typography>
        </TextLink>
      ),
    },
    {
      field: "ghost",
      headerName: "Ghost",
      renderCell: (params) => (
        <TextLink to={`/?ghost=${params.row.artistId}`} openInNewTab>
          <Typography fontSize={14} fontFamily={fonts.body}>
            Ghost
          </Typography>
        </TextLink>
      ),
    },
  ];

  const formattedUsers = (artists || []).map(
    ({ id, permalink, firstName, lastName, email, paypalEmail, city, country, vatRegistered, lastUploaded }) => ({
      id: uniqueId(),
      artistId: id,
      permalink,
      name: firstName + " " + lastName,
      email,
      paypalEmail,
      city,
      // @ts-ignore
      country: country && momentData.countries[country]?.name,
      vatNumber: vatRegistered?.vatNumber,
      lastUploaded,
    })
  );

  return (
    <Stack gap={2}>
      <Stack gap={1} direction="row" justifyContent="flex-end">
        <Select
          variant="filled"
          size="small"
          displayEmpty
          onChange={onCountryChange}
          value={selectedCountry}
          IconComponent={ArrowDropDownRounded}
          renderValue={(selected) =>
            selected ? momentData.countries[selected as keyof typeof momentData.countries]?.name : "Country"
          }
        >
          {sortedCountries.map((country) => (
            <MenuItem key={country.abbr} value={country.abbr}>
              {country.name}
            </MenuItem>
          ))}
        </Select>
        <Search inputValue={searchValue} setInputValue={setSearchValue} />
      </Stack>
      <DataGrid
        ref={gridRef}
        loading={loading}
        rows={formattedUsers}
        columns={columns}
        pageSizeOptions={[]}
        style={{ height: loading ? "70vh" : "auto", minHeight: "70vh" }}
        sx={dataGridStyles}
        pagination
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(newPage) => setPage(newPage.page)}
        rowCount={hasMoreArtists ? artists.length + 1 : artists.length}
      />
    </Stack>
  );
};

export default Artists;
